import React, { useCallback, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { useField } from 'formik'
import styled from 'styled-components'
import { useEventSlug } from 'hooks'
import { colors, _ } from 'utils'
import ConfirmModal from 'components/ConfirmModal'
import Icon, { IconOption } from 'components/Icon'
import { addModal } from 'features/modals/modalSlice'
import constants from '../../../constants/constants'
import { activateEvent } from '../graphql/mutation'

const S = {
    Wrapper: styled.div<{ isActive: boolean }>`
        margin: -38px -10px 20px -10px;
        padding: 0 20px;
        width: calc(100% + 20px);
        height: 56px;
        background-color: ${({ isActive }) => (isActive ? 'rgba(31, 155, 74, 0.05)' : colors.primaryOpacity(0.05))};
        display: flex;
        align-items: center;
        font-size: 14px;
        color: ${colors.grayTitle};
    `,
    Icon: styled(Icon)`
        margin: 0 10px 3px 0;
    `,
    Link: styled.span`
        margin-left: 10px;
        color: ${colors.primary};
        cursor: pointer;
        &:hover {
            text-decoration: underline;
        }
    `
}

enum Status {
    'NOT_ACTIVATED',
    'PENDING',
    'ACTIVATED'
}

const statusMessage = {
    [Status.NOT_ACTIVATED]: () => _('event.activation_status.inactive'),
    [Status.PENDING]: () => _('event.activation_status.pending'),
    [Status.ACTIVATED]: () => _('event.activation_status.active'),
}

const statusIcon: {[key in Status]: IconOption} = {
    [Status.NOT_ACTIVATED]: 'info',
    [Status.PENDING]: 'info',
    [Status.ACTIVATED]: 'checkCircle',
}

const statusIconColor = {
    [Status.NOT_ACTIVATED]: colors.primary,
    [Status.PENDING]: colors.warning,
    [Status.ACTIVATED]: colors.success,
}

const ActivationStatus = () => {
    const dispatch = useDispatch()
    const eventSlug = useEventSlug()
    const [paidField] = useField<boolean>({ name: 'paid' })
    const [
        isActivationRequestedField,,isActivationRequestedFieldHelper
    ] = useField<boolean>({ name: 'isActivationRequested' })
    const isPaid = paidField.value
    const isActivationRequested = isActivationRequestedField.value

    const status: Status = useMemo(() => {
        if (isPaid) {
            return Status.ACTIVATED
        }
        if (isActivationRequested && !constants.isLite) {
            return Status.PENDING
        }
        return Status.NOT_ACTIVATED
    }, [isActivationRequested, isPaid])

    const requestActivation = useCallback(async () => {
        await activateEvent(eventSlug)
        isActivationRequestedFieldHelper.setValue(true)
    }, [eventSlug, isActivationRequestedFieldHelper])

    const goToPartnerWebsite = useCallback(() => {
        // TODO: replace with the partner url
        window.location.href = 'https://elevent.ly/'
    }, [])

    const showConfirmModal = useCallback(() => {
        dispatch(addModal({
            Component: ({ close }: { close: () => void }) => (
                constants.isLite
                    ? (
                        <ConfirmModal
                            close={close}
                            title={_('event.go_to_partner_website.title')}
                            confirmDescription={_('event.go_to_partner_website.description')}
                            variantConfirm="primary"
                            onConfirm={goToPartnerWebsite}
                        />
                    )
                    : (
                        <ConfirmModal
                            close={close}
                            title={_('event.activation.title')}
                            confirmDescription={_('event.activation.description')}
                            variantConfirm="primary"
                            onConfirm={requestActivation}
                        />
                    )
            ),
            shouldNotWrap: true
        }))
    }, [dispatch, goToPartnerWebsite, requestActivation])

    return (
        <S.Wrapper isActive={isPaid}>
            <S.Icon
                icon={statusIcon[status]}
                color={statusIconColor[status]}
                size={20}
            />
            {_(statusMessage[status]())}
            {status === Status.NOT_ACTIVATED && (
                <S.Link onClick={showConfirmModal}>
                    {_('event.active_now')}
                </S.Link>
            )}
        </S.Wrapper>
    )
}

export default ActivationStatus
